import { Breadcrumb, defaultTestId, StepperLayout, StepperLayoutStep, useComputed, useModal } from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { useMemo } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ChangeEventWorkflow } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { guidedWorkflowFor } from "src/routes/projects/change-events/guided/workflows/registry";
import { createHierarchyBreadcrumbs } from "src/routes/projects/ProjectHeader";
import { GuidedChangeEventParams } from "src/routes/routesDef";
import { addEntityParam, createChangeEventsUrl } from "src/RouteUrls";
import { fail, queryResult } from "src/utils";
import { BaseChangeEventFormInput } from "./baseChangeEventForm";
import { DeleteOrExitConfirmationModal } from "./components/DeleteOrExitConfirmationModal";
import {
  GuidedChangeEventPage_ChangeEventFragment,
  GuidedChangeEventPage_ProjectFragment,
  useGuidedChangeEventPage_ChangeEventQuery,
  useGuidedChangeEventPage_ProjectQuery,
} from "./GuidedChangeEventPage.generated";

export function GuidedChangeEventPage() {
  const { projectId, idOrAdd } = useRouteParams<GuidedChangeEventParams>();
  const isAdd = idOrAdd === addEntityParam;
  // What the user selects in CreateGuidedChangeEventModal
  const { state } = useLocation() as { state: BaseChangeEventFormInput | null };
  const hasNothingToRun = isAdd && !state;

  const projectQuery = useGuidedChangeEventPage_ProjectQuery({ variables: { projectId }, skip: hasNothingToRun });
  const changeEventQuery = useGuidedChangeEventPage_ChangeEventQuery({
    variables: { changeEventId: idOrAdd, structureId: projectId },
    skip: isAdd,
    fetchPolicy: "cache-first",
  });

  // Keep it out of the query result so it does re-render when the new change event is saved
  // which changes to the id url.
  const workflow = state?.workflow ?? changeEventQuery.data?.changeEvent.workflow?.code;

  if (hasNothingToRun) {
    return <Navigate to={createChangeEventsUrl(projectId)} replace />;
  } else {
    return queryResult(projectQuery, ({ project }) => (
      <GuidedChangeEventPageView
        key={state ? [state.workflow, state.reasonId, state.type].join("-") : workflow}
        workflow={workflow ?? fail("This is not a guided change event")}
        project={project}
        maybeExistingChangeEvent={changeEventQuery.data?.changeEvent}
        maybeNewChangeEventFormInput={state ?? undefined}
      />
    ));
  }
}

type GuidedChangeEventPageViewProps = {
  project: GuidedChangeEventPage_ProjectFragment;
  workflow: ChangeEventWorkflow;
  /** Add mode only: the fields the user selected in CreateGuidedChangeEventModal, which seed the form the first step creates from. */
  maybeNewChangeEventFormInput?: BaseChangeEventFormInput;
  /** Edit mode. */
  maybeExistingChangeEvent?: GuidedChangeEventPage_ChangeEventFragment;
};

function GuidedChangeEventPageView(props: GuidedChangeEventPageViewProps) {
  const { project, workflow, maybeNewChangeEventFormInput, maybeExistingChangeEvent } = props;
  const navigate = useNavigate();
  const { openModal } = useModal();
  const workflowDef = guidedWorkflowFor(workflow)?.workflow ?? fail("Coming soon.");

  const formConfig = workflowDef.formConfig;
  const breadcrumbs: Breadcrumb[] = createHierarchyBreadcrumbs(project, createChangeEventsUrl);

  const form = useFormState({
    config: formConfig,
    init: {
      input: maybeExistingChangeEvent,
      map: (ce) => workflowDef.mapToForm?.(ce),
      ifUndefined: maybeNewChangeEventFormInput,
      onlyOnce: true,
    },
  });
  const actions = workflowDef.useActions({ form, projectId: project.id, project });

  // Every step comes from the workflow, and each one is gated behind the previous step's completion.
  const steps = useComputed<StepperLayoutStep[]>(() => {
    const completions = workflowDef.steps.map((s) => s.isComplete(form));
    return workflowDef.steps.map((s, i): StepperLayoutStep => {
      const explicitlyDisabled = s.disabled?.(form) ?? false;
      // The first step is always reachable; the rest wait on the step before them.
      const gatedByPrevious = i > 0 && !completions[i - 1];
      return {
        label: s.label,
        primaryDisabled: !completions[i],
        disabled: explicitlyDisabled || gatedByPrevious,
        content: s.render(form, project),
        onContinue: () => actions.onContinue?.[s.value]?.(),
      };
    });
  }, [form, project, workflowDef]);

  // If defined, the initial step to navigate to when the workflow is first mounted.
  const maybeInitialStep = useMemo(() => {
    const maybeInitialStep = workflowDef.initialStep?.({ form, projectId: project.id, project });
    const step = workflowDef.steps.find((s) => s.value === maybeInitialStep);
    return step && defaultTestId(step.label);
  }, [form, project, workflowDef]);

  function onCancel() {
    const changeEventId = form.id.value;
    if (!changeEventId) return navigate(createChangeEventsUrl(project.id));
    openModal({ content: <DeleteOrExitConfirmationModal changeEventId={changeEventId} projectId={project.id} /> });
  }

  return (
    <StepperLayout
      title="Create Change Event"
      documentTitleSuffix={project.name}
      breadcrumbs={{ breadcrumbs }}
      steps={steps}
      onCancel={onCancel}
      onSaveAndExit={actions.onSaveAndExit}
      completeLabel="Create"
      onComplete={() => actions.onSubmit()}
      isDirty={() => form.dirty && !form.id.value}
      defaultStep={maybeInitialStep}
      // Don't show the "unsaved changes" modal when the user is just filtering: a step's table writes its filter/search to the
      // url, which looks like a navigation even though we never left the page.
      allowNavigation={({ currentLocation, nextLocation }) => currentLocation.pathname === nextLocation.pathname}
    />
  );
}
